import classNames from "classnames";
import PageBox from "PFComponents/box/page_box";
import { LoadingDots } from "PFComponents/loading_dots";
import { PillTabs } from "PFComponents/tabs/pill_tabs";
import { Typography } from "PFComponents/typography";
import { Account } from "PFTypes/account";
import { ConfigTemplateType } from "PFTypes/config_template";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { FiltersAndOrderConfigContextProvider } from "./filters_and_order_config_context";
import css from "./filters_and_order_config_page.module.scss";
import { useFiltersTab } from "./hooks/use_filters_tab";
import { useOrderTab } from "./hooks/use_order_tab";
import { FiltersConfigForm } from "./parts/filters_config_form";
import { OrderConfigForm } from "./parts/order_config_form";
import { TargetSelect } from "./parts/target_select";

type FiltersAndOrderConfigPageProps = {
  account: Account;
};

export const FiltersAndOrderConfigPage = ({ account }: FiltersAndOrderConfigPageProps) => {
  const { t } = useTranslation("saasAdmin", { keyPrefix: "filtersConfig" });

  const [selectedTab, setSelectedTab] = useState<ConfigTemplateType>(ConfigTemplateType.Filters);

  const filtersTabData = useFiltersTab(account.id, selectedTab === ConfigTemplateType.Filters);
  const orderTabData = useOrderTab(account.id, selectedTab === ConfigTemplateType.Order);

  const {
    target,
    setTarget,
    config: { data: configData, isLoading: isConfigLoading },
    template: { data: configTemplateData, isLoading: isConfigTemplateLoading },
    isSubmitting
  } = useMemo(
    () =>
      ({
        [ConfigTemplateType.Filters]: filtersTabData,
        [ConfigTemplateType.Order]: orderTabData
      }[selectedTab]),
    [selectedTab, filtersTabData, orderTabData]
  );

  const handleSelect = (selectedOptionId: ConfigTemplateType) => {
    setTarget(selectedOptionId);
  };

  const TABS = useMemo(
    () => [
      { id: ConfigTemplateType.Filters, label: t("tabs.filters") },
      { id: ConfigTemplateType.Order, label: t("tabs.order") }
    ],
    [t]
  );

  const ConfigForm = {
    [ConfigTemplateType.Filters]: FiltersConfigForm,
    [ConfigTemplateType.Order]: OrderConfigForm
  }[selectedTab];

  const isConfigTemplateReady = configTemplateData && !isConfigTemplateLoading;
  const isConfigDataReady = configData && !isConfigLoading;

  return (
    <div>
      <header className={css.header}>
        <Typography variant="h1">{t("title")}</Typography>
        <Typography variant="h4" className={css.subheader}>
          {`(${account.name})`}
        </Typography>
      </header>
      <PageBox className={classNames(css.pageBox, { [css.updating]: isSubmitting })}>
        <PillTabs<ConfigTemplateType> tabs={TABS} selectedId={selectedTab} onTabClick={setSelectedTab} />
        {isConfigTemplateReady && (
          <>
            <TargetSelect
              key={selectedTab}
              template={configTemplateData}
              selected={target}
              onSelect={handleSelect}
            />
            {!!target &&
              (isConfigDataReady ? (
                <FiltersAndOrderConfigContextProvider order={orderTabData} filters={filtersTabData}>
                  <ConfigForm key={target} />
                </FiltersAndOrderConfigContextProvider>
              ) : (
                <LoadingDots circlesEnabled pageViewEnabled />
              ))}
          </>
        )}
      </PageBox>
    </div>
  );
};
